import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import ErrorIcon from '@mui/icons-material/Error';

const NotificationContext = createContext(null);

function SlideUp(props) { return <Slide {...props} direction="up" />; }

export function NotificationProvider({ children, maxSnack = 3, autoHideDuration = 6000 }) {
  const [queue, setQueue] = useState([]); // { id, message, severity }
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const processQueue = useCallback(() => {
    if (current || queue.length === 0) return;
    const next = queue[0];
    setCurrent(next);
    setOpen(true);
  }, [queue, current]);

  const enqueue = useCallback((msg, severity = 'info') => {
    setQueue(q => {
      const next = [...q, { id: Date.now() + Math.random(), message: msg, severity }];
      // Trim to maxSnack backlog (keep newest)
      if (next.length > maxSnack) next.splice(0, next.length - maxSnack);
      return next;
    });
  }, [maxSnack]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setQueue(q => q.slice(1));
    setCurrent(null);
  };

  React.useEffect(() => { processQueue(); }, [processQueue, queue, current]);

  React.useEffect(() => {
    const handleGlobalNotify = (event) => {
      const detail = event?.detail || {};
      const message = String(detail.message || '').trim();
      const severity = String(detail.severity || 'info').toLowerCase();
      if (!message) return;
      const allowed = ['success', 'error', 'warning', 'info'];
      enqueue(message, allowed.includes(severity) ? severity : 'info');
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('app:notify', handleGlobalNotify);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app:notify', handleGlobalNotify);
      }
    };
  }, [enqueue]);

  const api = useMemo(() => ({
    notify: enqueue,
    success: (m) => enqueue(m, 'success'),
    error: (m) => enqueue(m, 'error'),
    warning: (m) => enqueue(m, 'warning'),
    info: (m) => enqueue(m, 'info')
  }), [enqueue]);

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <Snackbar
        key={current ? current.id : undefined}
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        TransitionComponent={SlideUp}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionProps={{ onExited: handleExited }}
        sx={{ zIndex: (theme) => ((theme.zIndex?.snackbar || 1400) + 100) }}
      >
        {current && (
          <Alert onClose={handleClose} severity={current.severity} sx={{ width: '100%' }} variant="filled">
            {current.message}
          </Alert>
        )}
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotify must be used within NotificationProvider');
  return ctx;
}
